import type { FilterDef, FilterValues } from "@/lib/table"

import {
  STATUS_LABEL,
  assigneeOptions,
  submitterOptions,
  type QueueFilters,
  type QueueRow,
  type QueueStatus,
} from "./queue-data"

/*
   Queue filter schema — declarative. Consumed by <FilterButton>,
   <FilterPanel>, <ActiveFilterChips>, and mapped into QueueFilters for
   the data query. Adding a filter is one entry here + one field in
   QueueFilters + one branch in `filtersToQuery` below.

   The assignee/submitter dropdown options are derived from the loaded queue
   rows — the caller passes them in so the schema is a pure function of data,
   not a coupling to a specific fetch. When rows is empty (initial load, or
   the caller has no access), those dropdowns render with an empty option list.
*/

export function buildQueueFilterSchema(rows: readonly QueueRow[] = []): FilterDef[] {
  return [
    {
      id: "status",
      label: "Status",
      kind: "select",
      options: (Object.keys(STATUS_LABEL) as QueueStatus[]).map((s) => ({
        value: s,
        label: STATUS_LABEL[s],
      })),
      placeholder: "Any status",
    },
    {
      id: "assignee",
      label: "Assignee",
      kind: "select",
      multi: true,
      options: assigneeOptions(rows),
    },
    {
      id: "submitter",
      label: "Submitter",
      kind: "select",
      multi: true,
      options: submitterOptions(rows),
    },
    {
      id: "findingsState",
      label: "Findings",
      kind: "select",
      options: [
        { value: "outstanding", label: "Has outstanding" },
        { value: "corrected", label: "All corrected" },
        { value: "none", label: "No findings yet" },
      ],
      placeholder: "Any",
    },
    {
      id: "hasUnread",
      label: "Comms",
      kind: "boolean",
      hint: "Only rows with unread messages",
    },
    {
      id: "updatedAt",
      label: "Updated",
      kind: "date-range",
    },
  ]
}

/** Map the generic FilterValues bag into typed QueueFilters for the query. */
export function filtersToQuery(values: FilterValues): QueueFilters {
  const q: QueueFilters = {}
  if (typeof values.status === "string" && values.status.length > 0) {
    q.status = values.status as QueueStatus
  }
  if (Array.isArray(values.assignee) && values.assignee.length > 0) {
    q.assignee = values.assignee
  }
  if (Array.isArray(values.submitter) && values.submitter.length > 0) {
    q.submitter = values.submitter
  }
  if (typeof values.findingsState === "string" && values.findingsState.length > 0) {
    q.findingsState = values.findingsState as QueueFilters["findingsState"]
  }
  if (values.hasUnread === true) {
    q.hasUnread = true
  }
  if (
    values.updatedAt &&
    typeof values.updatedAt === "object" &&
    !Array.isArray(values.updatedAt) &&
    "from" in values.updatedAt
  ) {
    const range = values.updatedAt as { from: string | null; to: string | null }
    if (range.from || range.to) q.updatedAt = range
  }
  return q
}
